///
/// EarthBrowser - Data Browser View
///
/// Main component for browsing data with any provider
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { useState, useEffect, useCallback, useMemo, useRef, useImperativeHandle, forwardRef } from "react"
import { DataTable } from "./DataTable"
import { DataBrowserControls, type DataBrowserControlsHandle } from "./DataBrowserControls"
import type { IDataProvider, DataQuery, DataRow, SortSpec, ColumnMetadata } from "@/data-providers/types"
import { setSearch, setSort, setPagination } from "@/data-providers/queryBuilder"

export interface DataBrowserViewProps {
	// Data provider
	provider: IDataProvider

	// Initial query state
	initialQuery?: DataQuery

	// Callbacks
	onRowClick?: (row: DataRow) => void
	onRowDoubleClick?: (row: DataRow) => void
	onQueryChange?: (query: DataQuery) => void
	onRefresh?: () => void

	// Custom actions
	actions?: React.ReactNode

	// Sync status
	totalDatabaseCount?: number
	lastSyncTime?: string

	// Display options
	searchPlaceholder?: string
	disableStickyHeader?: boolean
	hideControls?: boolean // Hide search/pagination controls entirely
}

export interface DataBrowserViewHandle {
	focusSearch: () => void
}

export const DataBrowserView = forwardRef<DataBrowserViewHandle, DataBrowserViewProps>(function DataBrowserView({
	provider,
	initialQuery = {},
	onRowClick,
	onRowDoubleClick,
	onQueryChange,
	onRefresh,
	actions,
	totalDatabaseCount,
	lastSyncTime,
	searchPlaceholder,
	disableStickyHeader = false,
	hideControls = false,
}, ref) {
	const controlsRef = useRef<DataBrowserControlsHandle>(null)

	// Expose focus method to parent
	useImperativeHandle(ref, () => ({
		focusSearch: () => {
			controlsRef.current?.focusSearch()
		}
	}), [])

	// Query state
	const [query, setQuery] = useState<DataQuery>(initialQuery)
	const pageSize = 200 // Hard-coded page size
	const [currentPage, setCurrentPage] = useState(1)

	// Data state
	const [rows, setRows] = useState<DataRow[]>([])
	const [columns, setColumns] = useState<ColumnMetadata[]>([])
	const [totalCount, setTotalCount] = useState(0)
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Initialize provider
	useEffect(() => {
		const init = async () => {
			try {
				if (provider.initialize) {
					await provider.initialize()
				}

				// Load columns
				const cols = await provider.getColumns()
				setColumns(cols)
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to initialize provider')
			}
		}

		void init()
	}, [provider])

	// Execute query
	const executeQuery = useCallback(async (q: DataQuery) => {
		setIsLoading(true)
		setError(null)

		try {
			const result = await provider.query(q)
			setRows(result.rows)
			setTotalCount(result.totalCount)
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Query failed')
			setRows([])
			setTotalCount(0)
		} finally {
			setIsLoading(false)
		}
	}, [provider])

	// Update query and execute
	const updateQuery = useCallback((newQuery: DataQuery) => {
		setQuery(newQuery)
		onQueryChange?.(newQuery)
		void executeQuery(newQuery)
	}, [executeQuery, onQueryChange])

	// Initial data load
	useEffect(() => {
		const initialQueryWithPagination = setPagination(query, 0, pageSize)
		updateQuery(initialQueryWithPagination)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// Handle search change
	const handleSearchChange = useCallback((searchQuery: string) => {
		const newQuery = setSearch(query, searchQuery)
		const withPagination = setPagination(newQuery, 0, pageSize)
		setCurrentPage(1) // Reset to first page
		updateQuery(withPagination)
	}, [query, pageSize, updateQuery])

	// Handle sort change
	const handleSortChange = useCallback((sort: SortSpec[]) => {
		const newQuery = setSort(query, sort)
		updateQuery(newQuery)
	}, [query, updateQuery])

	// Handle page change
	const handlePageChange = useCallback((page: number) => {
		const offset = (page - 1) * pageSize
		const newQuery = setPagination(query, offset, pageSize)
		setCurrentPage(page)
		updateQuery(newQuery)
	}, [query, pageSize, updateQuery])

	// Calculate total pages
	const totalPages = useMemo(() => {
		return Math.ceil(totalCount / pageSize)
	}, [totalCount, pageSize])

	// Error state
	if (error) {
		return (
			<div className="w-full h-full flex items-center justify-center">
				<div className="text-center">
					<p className="text-red-400 font-semibold mb-2">Error</p>
					<p className="text-white/60 text-sm">{error}</p>
				</div>
			</div>
		)
	}

	return (
		// h-full for definite parents (floating windows); flex-1/min-h-0 so
		// that inside a content-sized flex column capped by max-height (the
		// data dock panel) the view shrinks to fit and the table scrolls
		<div className="flex flex-col h-full flex-1 min-h-0">
			{/* Controls */}
			{!hideControls && (
				<DataBrowserControls
					ref={controlsRef}
					searchQuery={query.search?.query}
					onSearchChange={provider.capabilities.supportsFullTextSearch ? handleSearchChange : undefined}
					searchPlaceholder={searchPlaceholder || `Search ${provider.name}...`}
					currentPage={currentPage}
					totalPages={totalPages}
					pageSize={pageSize}
					totalCount={totalCount}
					totalDatabaseCount={totalDatabaseCount}
					lastSyncTime={lastSyncTime}
					onPageChange={handlePageChange}
					onRefresh={onRefresh}
					hasFilters={!!query.filters}
					actions={actions}
				/>
			)}

			{/* Data Table */}
			<div className="flex-1 min-h-0 overflow-hidden flex flex-col">
				<DataTable
					rows={rows}
					columns={columns}
					sort={query.sort}
					onSortChange={provider.capabilities.supportsSorting ? handleSortChange : undefined}
					onRowClick={onRowClick}
					onRowDoubleClick={onRowDoubleClick}
					maxHeight="100%"
					stickyHeader={!disableStickyHeader}
					isLoading={isLoading}
					emptyMessage={query.search?.query ? "No results match your search" : "No data available"}
				/>
			</div>
		</div>
	)
})

